import {
  isCrmContextMessage as isHostCrmContextMessage,
  normalizeAccountId,
  normalizeCrmSession,
  validateHubOrigin,
} from '../../routes/dashboard/crm/crm-host-protocol.js';

const CONVERSATION_ID_PATTERN = /^[0-9]{1,20}$/u;
const OPPORTUNITY_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/u;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;

function isRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value, required, optional = []) {
  if (!isRecord(value)) return false;
  const allowed = new Set([...required, ...optional]);
  const keys = Reflect.ownKeys(value);
  return (
    keys.every(key => typeof key === 'string' && allowed.has(key)) &&
    required.every(key => Object.prototype.hasOwnProperty.call(value, key))
  );
}

function normalizeConversationId(value) {
  const normalized = normalizeAccountId(value);
  return normalized && CONVERSATION_ID_PATTERN.test(normalized)
    ? normalized
    : null;
}

function normalizeOpportunityId(value) {
  return typeof value === 'string' && OPPORTUNITY_ID_PATTERN.test(value.trim())
    ? value.trim()
    : null;
}

function normalizeOptionalId(value) {
  if (value === undefined || value === null) return null;
  return normalizeAccountId(value);
}

export function normalizeConversationContext(value) {
  if (
    !hasOnlyKeys(
      value,
      ['type', 'accountId', 'conversationId'],
      ['contactId', 'agentId']
    ) ||
    value.type !== 'crm:context'
  ) {
    return null;
  }

  const accountId = normalizeAccountId(value.accountId);
  const conversationId = normalizeConversationId(value.conversationId);
  const contactId = normalizeOptionalId(value.contactId);
  const agentId = normalizeOptionalId(value.agentId);
  if (
    !accountId ||
    !conversationId ||
    (value.contactId !== undefined && value.contactId !== null && !contactId) ||
    (value.agentId !== undefined && value.agentId !== null && !agentId)
  ) {
    return null;
  }

  const normalized = {
    type: 'crm:context',
    accountId,
    conversationId,
  };
  if (contactId) normalized.contactId = contactId;
  if (agentId) normalized.agentId = agentId;
  return normalized;
}

export function isCrmContextMessage(value) {
  return normalizeConversationContext(value) !== null;
}

export function isCrmReadyMessage(value) {
  return hasOnlyKeys(value, ['type']) && value.type === 'crm:ready';
}

export function isCrmSessionMessage(value) {
  return (
    hasOnlyKeys(value, ['type', 'token', 'expiresAt', 'accountId']) &&
    value.type === 'crm:session' &&
    typeof value.token === 'string' &&
    TOKEN_PATTERN.test(value.token) &&
    normalizeAccountId(value.accountId) !== null &&
    Number.isSafeInteger(value.expiresAt) &&
    value.expiresAt > 0
  );
}

export function isCrmNavigateMessage(value) {
  const conversationNavigation =
    hasOnlyKeys(value, ['type', 'conversationId']) &&
    value.type === 'crm:navigate' &&
    normalizeConversationId(value.conversationId) !== null;
  const opportunityNavigation =
    hasOnlyKeys(value, ['type', 'accountId', 'opportunityId']) &&
    value.type === 'crm:navigate' &&
    normalizeAccountId(value.accountId) !== null &&
    normalizeOpportunityId(value.opportunityId) !== null;
  return conversationNavigation || opportunityNavigation;
}

export function isCrmOpportunityNavigateMessage(value, expectedAccountId) {
  const accountId = normalizeAccountId(expectedAccountId);
  return (
    accountId !== null &&
    hasOnlyKeys(value, ['type', 'accountId', 'opportunityId']) &&
    value.type === 'crm:navigate' &&
    normalizeAccountId(value.accountId) === accountId &&
    normalizeOpportunityId(value.opportunityId) !== null
  );
}

export function isCrmOpenOpportunityMessage(value, expectedAccountId) {
  const accountId = normalizeAccountId(expectedAccountId);
  return (
    accountId !== null &&
    hasOnlyKeys(value, ['type', 'accountId', 'opportunityId']) &&
    value.type === 'crm:open-opportunity' &&
    normalizeAccountId(value.accountId) === accountId &&
    normalizeOpportunityId(value.opportunityId) !== null
  );
}

export function isCrmClearMessage(value) {
  return hasOnlyKeys(value, ['type']) && value.type === 'crm:clear';
}

function isTrustedMessage(event, expectedOrigin, expectedSource, validator) {
  const origin = validateHubOrigin(expectedOrigin);
  return Boolean(
    origin &&
    event &&
    event.origin === origin &&
    event.source === expectedSource &&
    validator(event.data)
  );
}

export function isTrustedCrmMessage(
  event,
  expectedOrigin,
  expectedSource,
  expectedAccountId
) {
  const origin = validateHubOrigin(expectedOrigin);
  if (
    !origin ||
    !event ||
    event.origin !== origin ||
    event.source !== expectedSource
  ) {
    return false;
  }
  const data = event.data;
  if (expectedAccountId !== undefined) {
    const accountId = normalizeAccountId(expectedAccountId);
    if (!accountId) return false;
    if (
      isCrmContextMessage(data) &&
      normalizeConversationContext(data).accountId !== accountId
    ) {
      return false;
    }
    if (
      isCrmSessionMessage(data) &&
      normalizeAccountId(data.accountId) !== accountId
    ) {
      return false;
    }
    if (
      hasOnlyKeys(data, ['type', 'accountId', 'opportunityId']) &&
      data.type === 'crm:navigate' &&
      normalizeAccountId(data.accountId) !== accountId
    ) {
      return false;
    }
    if (
      hasOnlyKeys(data, ['type', 'accountId', 'opportunityId']) &&
      data.type === 'crm:open-opportunity' &&
      normalizeAccountId(data.accountId) !== accountId
    ) {
      return false;
    }
  }
  return (
    isCrmReadyMessage(data) ||
    isCrmContextMessage(data) ||
    isCrmSessionMessage(data) ||
    isCrmNavigateMessage(data) ||
    isCrmClearMessage(data)
  );
}

export function isTrustedCrmReadyMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  return isTrustedMessage(
    event,
    expectedOrigin,
    expectedSource,
    isCrmReadyMessage
  );
}

export function isTrustedCrmContextMessage(
  event,
  expectedOrigin,
  expectedSource,
  expectedAccountId
) {
  const origin = validateHubOrigin(expectedOrigin);
  const context = event?.data;
  return Boolean(
    origin &&
    event &&
    event.origin === origin &&
    event.source === expectedSource &&
    isHostCrmContextMessage(context) &&
    (expectedAccountId === undefined ||
      normalizeConversationContext(context).accountId ===
        normalizeAccountId(expectedAccountId))
  );
}

export function isTrustedCrmSessionMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  return isTrustedMessage(
    event,
    expectedOrigin,
    expectedSource,
    isCrmSessionMessage
  );
}

export function isTrustedCrmNavigateMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  return isTrustedMessage(
    event,
    expectedOrigin,
    expectedSource,
    isCrmNavigateMessage
  );
}

export function isTrustedCrmOpportunityNavigateMessage(
  event,
  expectedOrigin,
  expectedSource,
  expectedAccountId
) {
  return isTrustedMessage(event, expectedOrigin, expectedSource, value =>
    isCrmOpportunityNavigateMessage(value, expectedAccountId)
  );
}

export function isTrustedCrmOpenOpportunityMessage(
  event,
  expectedOrigin,
  expectedSource,
  expectedAccountId
) {
  return isTrustedMessage(event, expectedOrigin, expectedSource, value =>
    isCrmOpenOpportunityMessage(value, expectedAccountId)
  );
}

export function isTrustedCrmClearMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  return isTrustedMessage(
    event,
    expectedOrigin,
    expectedSource,
    isCrmClearMessage
  );
}

export function buildCrmContextMessage(value, expectedAccountId) {
  const context = normalizeConversationContext({
    type: 'crm:context',
    ...value,
  });
  const accountId = normalizeAccountId(expectedAccountId);
  if (!context || !accountId || context.accountId !== accountId) return null;
  return context;
}

export function buildCrmNavigateMessage(conversationId) {
  const normalizedConversationId = normalizeConversationId(conversationId);
  return normalizedConversationId
    ? { type: 'crm:navigate', conversationId: normalizedConversationId }
    : null;
}

export function buildCrmOpportunityNavigateMessage(accountId, opportunityId) {
  const normalizedAccountId = normalizeAccountId(accountId);
  const normalizedOpportunityId = normalizeOpportunityId(opportunityId);
  return normalizedAccountId && normalizedOpportunityId
    ? {
        type: 'crm:navigate',
        accountId: normalizedAccountId,
        opportunityId: normalizedOpportunityId,
      }
    : null;
}

export function buildCrmOpenOpportunityMessage(accountId, opportunityId) {
  const normalizedAccountId = normalizeAccountId(accountId);
  const normalizedOpportunityId = normalizeOpportunityId(opportunityId);
  return normalizedAccountId && normalizedOpportunityId
    ? {
        type: 'crm:open-opportunity',
        accountId: normalizedAccountId,
        opportunityId: normalizedOpportunityId,
      }
    : null;
}

export function isCurrentCrmConversation(
  value,
  routeAccountId,
  routeConversationId
) {
  const context = normalizeConversationContext(value);
  return Boolean(
    context &&
    context.accountId === normalizeAccountId(routeAccountId) &&
    context.conversationId === normalizeConversationId(routeConversationId)
  );
}

export function normalizeCrmConversationSession(
  value,
  accountId,
  expectedOrigin
) {
  return normalizeCrmSession(value, accountId, expectedOrigin);
}
